'use client';

import { useState, useEffect } from 'react';
import type { BrokerConfig, BrokerFieldConfig } from '@/config/brokers';

interface BrokerFormProps {
  broker: BrokerConfig;
  onSubmit: (data: Record<string, string>) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export default function BrokerForm({
  broker,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error = null,
}: BrokerFormProps) {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Initialize form data with empty values for all fields
  useEffect(() => {
    const initialData: Record<string, string> = {};
    broker.fields.forEach((field) => {
      initialData[field.key] = '';
    });
    setFormData(initialData);
    setFieldErrors({});
  }, [broker]);

  const handleFieldChange = (field: BrokerFieldConfig, value: string) => {
    let processedValue = value;

    // Apply field-specific transformations
    if (field.type === 'tel' || field.type === 'number') {
      // Only allow digits
      processedValue = value.replace(/\D/g, '');
    }

    // Apply maxLength constraint
    if (field.maxLength) {
      processedValue = processedValue.slice(0, field.maxLength);
    }

    setFormData((prev) => ({
      ...prev,
      [field.key]: processedValue,
    }));

    // Clear error for this field when user starts typing
    if (fieldErrors[field.key]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field.key];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    broker.fields.forEach((field) => {
      const value = formData[field.key] || '';

      // Required field validation
      if (field.required && !value.trim()) {
        errors[field.key] = `${field.label} is required`;
        return;
      }

      // Min length validation
      if (field.minLength && value.length < field.minLength) {
        errors[field.key] = `${field.label} must be at least ${field.minLength} characters`;
        return;
      }

      // Max length validation
      if (field.maxLength && value.length > field.maxLength) {
        errors[field.key] = `${field.label} must be at most ${field.maxLength} characters`;
        return;
      }

      // Custom validation
      if (field.validate && value.trim()) {
        const validationError = field.validate(value);
        if (validationError) {
          errors[field.key] = validationError;
          return;
        }
      }

      // Pattern validation
      if (field.pattern && value.trim()) {
        const regex = new RegExp(field.pattern);
        if (!regex.test(value)) {
          errors[field.key] = `${field.label} format is invalid`;
          return;
        }
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onSubmit(formData);
  };

  const getFieldIcon = (fieldKey: string) => {
    const iconClass = 'w-5 h-5';
    switch (fieldKey) {
      case 'api_key':
      case 'client_id':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        );
      case 'api_secret':
      case 'client_secret':
      case 'authorization_key':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      case 'mobile':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'ucc':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
          </svg>
        );
      case 'mpin':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      case 'application_token':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderField = (field: BrokerFieldConfig) => {
    const value = formData[field.key] || '';
    const error = fieldErrors[field.key];
    const hasError = !!error;
    const isValid = value && !error && (field.minLength ? value.length >= field.minLength : true);

    const inputClasses = `
      w-full px-4 py-3 pl-11 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2
      ${
        hasError
          ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 focus:ring-red-500/20 focus:border-red-500'
          : isValid
          ? 'border-emerald-300 dark:border-emerald-700 bg-white dark:bg-zinc-900 focus:ring-emerald-500/20 focus:border-emerald-500'
          : 'border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 focus:ring-emerald-500/20 focus:border-emerald-500'
      }
      text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const inputElement = (
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 dark:text-zinc-500">
          {getFieldIcon(field.key)}
        </div>
        <input
          id={field.key}
          type={field.type}
          value={value}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          minLength={field.minLength}
          pattern={field.pattern}
          className={inputClasses}
          required={field.required}
          disabled={isSubmitting}
        />
        {isValid && !hasError && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
    );

    return (
      <div key={field.key} className="space-y-2">
        <label
          htmlFor={field.key}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-700 dark:text-zinc-300"
        >
          {field.label}
          {field.required && <span className="text-red-500">*</span>}
        </label>
        
        {field.prefix ? (
          <div className="flex items-center gap-0">
            <span className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/60 border border-r-0 border-zinc-300 dark:border-zinc-700 rounded-l-lg text-zinc-700 dark:text-zinc-300 font-medium">
              {field.prefix}
            </span>
            <div className="flex-1">{inputElement}</div>
          </div>
        ) : (
          inputElement
        )}
        
        {hasError && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 animate-shake">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        
        {field.helpText && !hasError && (
          <div className="flex items-start gap-2 text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-800/60 p-2 rounded-lg">
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>{field.helpText}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg animate-shake">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-red-800 dark:text-red-200 mb-1">Error</p>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {broker.fields.map((field, index) => (
          <div key={field.key} style={{ animationDelay: `${index * 50}ms` }} className="animate-fadeIn">
            {renderField(field)}
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-4 border-t border-zinc-200 dark:border-zinc-800">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 rounded-lg font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Connecting...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Connect Broker</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}

